/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { IDetailsList } from '@fluentui/react/lib/DetailsList';
import { ValidationState } from '@fluentui/react/lib/Pickers';
import * as React from 'react';
import { RecipientListMobileComponent } from '../../../mobile/Esign/RecipientList/RecipientListMobile.component';
import { SignApprovalType } from '../../../service/gen/esign';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { InputState } from '../utils/forms';
import { copy, emptyRecipient, Recipient, RecipientTypeLabel } from './Recipient';
import { RecipientListComponent, RecipientListComponentProps } from './RecipientList.component';

export interface RecipientListContainerProps {
    signApprovalType: SignApprovalType;
    availableRoles: string[];
    required?: boolean;
    recipientsInput: InputState<Recipient[]>;
    completeInOrderInput: InputState<boolean>;
}

export const RecipientListContainer: React.FC<RecipientListContainerProps> = (
    props: React.PropsWithChildren<RecipientListContainerProps>
): JSX.Element => {
    const { signApprovalType, availableRoles, completeInOrderInput, recipientsInput, required } = props;

    const host = getAppHost();

    const toggleCompleteInOrder = React.useCallback(
        (): void => completeInOrderInput.setValue((prevState: boolean): boolean => !prevState),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [completeInOrderInput.setValue]
    );

    const showNameField = signApprovalType === SignApprovalType.Docusign;

    const isAddRecipientDisabled =
        recipientsInput.value &&
        (recipientsInput.value.length >= recipientsInput.maxLength ||
            recipientsInput.value.some((recipient: Recipient): boolean => {
                const isNameInvalid = showNameField && (!recipient.name || recipient.name.trim() === '');

                const isPersonaInvalid =
                    !recipient.persona ||
                    recipient.persona.ValidationState === ValidationState.invalid ||
                    recipient.persona.ValidationState === ValidationState.warning;

                return isNameInvalid || isPersonaInvalid;
            }));

    // Return a recipient list, which is a copy of recipients parameter, without the removed recipient
    const getRemainedRecipients = (recipients: Recipient[], removedRecipient: Recipient): Recipient[] =>
        recipients.reduce((results: Recipient[], current: Recipient): Recipient[] => {
            if (current !== removedRecipient) {
                results.push(copy(current, (results.length + 1).toString()));
            }
            return results;
        }, []);

    // Return the same recipients reference, where a copy of recipient is added to the specified position
    const getAggregatedRecipients = (recipients: Recipient[], position: number, recipient: Recipient): Recipient[] => {
        recipients.splice(position, 0, copy(recipient));
        recipients.forEach((item: Recipient, idx: number, items: Recipient[]): void => {
            items[idx].order = (idx + 1).toString();
        });
        return recipients;
    };

    // Add a recipient
    const addRecipient = (ref?: React.RefObject<IDetailsList>): void =>
        recipientsInput.setValue((recipients: Recipient[]): Recipient[] => {
            const newRecipients = recipients.map((recipient: Recipient): Recipient => copy(recipient));

            if (ref != null) {
                if (recipients.length > 0) {
                    ref.current.focusIndex(recipients.length - 1);
                }
            }

            newRecipients.push(copy(emptyRecipient, (recipients.length + 1).toString(), true));
            return newRecipients;
        });

    // Remove a recipient
    const removeRecipient = (recipient: Recipient): void =>
        recipientsInput.setValue((recipients: Recipient[]): Recipient[] =>
            getRemainedRecipients(recipients, recipient)
        );

    // Update a recipient field
    const updateRecipient = (recipient: Recipient, newRecipient: Recipient): void =>
        recipientsInput.setValue((recipients: Recipient[]): Recipient[] =>
            recipients.map((recip: Recipient): Recipient => (recip === recipient ? copy(newRecipient) : copy(recip)))
        );

    // Move a recipient to the position of another recipient
    const moveRecipient = (recipient: Recipient, toRecipient: Recipient): void =>
        recipientsInput.setValue((recipients: Recipient[]): Recipient[] => {
            const fromIndex = recipients.indexOf(recipient);
            const toIndex = recipients.indexOf(toRecipient);

            if (recipient !== toRecipient && fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
                const newRecipients = getRemainedRecipients(recipients, recipient);
                return getAggregatedRecipients(newRecipients, toIndex, recipient);
            }

            return recipients;
        });

    // Move a recipient to the position where the order indicates
    const moveRecipientWithOrder = (recipient: Recipient, order: string): void =>
        recipientsInput.setValue((recipients: Recipient[]): Recipient[] => {
            const fromIndex = recipients.indexOf(recipient);

            if (fromIndex !== -1) {
                const newRecipients = getRemainedRecipients(recipients, recipient);
                const parsedOrder = order ? parseInt(order, 10) : 0;
                const findIndex = newRecipients.findIndex(
                    (recip: Recipient): boolean => parseInt(recip.order, 10) >= parsedOrder
                );
                const insertPosition = findIndex === -1 ? newRecipients.length : findIndex;
                return getAggregatedRecipients(newRecipients, insertPosition, recipient);
            }

            return recipients;
        });

    const recipientTypeLabelMap = {
        [SignApprovalType.Adobe]: RecipientTypeLabel.Role,
        [SignApprovalType.Docusign]: RecipientTypeLabel.Action,
    };

    const recipientListProps: RecipientListComponentProps = {
        recipients: recipientsInput.value,
        availableRoles,
        isAddRecipientDisabled,
        completeInOrder: completeInOrderInput.value,
        toggleCompleteInOrder,
        moveRecipient,
        moveRecipientWithOrder,
        updateRecipient,
        addRecipient,
        removeRecipient,
        required,
        recipientTypeLabel: recipientTypeLabelMap[signApprovalType],
        showNameField,
    };

    return host.isMobile() ? (
        <RecipientListMobileComponent {...recipientListProps} />
    ) : (
        <RecipientListComponent {...recipientListProps} />
    );
};
